"use client";
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const features = [
{ icon: '🏆', label: 'ISO Certified Operations' },
{ icon: '🌍', label: '15+ Source Countries' },
{ icon: '⚡', label: '72-Hour Rapid Deployment' },
{ icon: '📋', label: 'Full Visa & Compliance' }];


export default function AboutSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} className="relative py-24 overflow-hidden" style={{ background: '#020d1f' }}>
      {/* Mesh glow */}
      <div
        className="glow-orb w-[500px] h-[500px] top-0 right-0 opacity-30"
        style={{ background: 'rgba(26,86,176,0.3)' }} />
      
      <div className="dot-grid absolute inset-0 opacity-30 pointer-events-none" />
      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-center">
          {/* Photo — 60% */}
          <div className="lg:col-span-3 reveal-hidden stagger-1">
            <div className="relative">
              {/* Offset border */}
              <div
                className="absolute -bottom-4 -right-4 w-full h-full rounded-2xl z-0"
                style={{ border: '2px solid rgba(0,194,255,0.3)', borderRadius: '16px' }} />
              
              {/* Blue accent corner */}
              <div
                className="absolute -top-4 -left-4 w-16 h-16 rounded-xl z-20"
                style={{ background: 'linear-gradient(135deg, #00c2ff, #1a56b0)' }} />
              

              <div className="relative z-10 rounded-2xl overflow-hidden" style={{ height: '500px' }}>
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1213389da-1772906009565.png"
                  alt="Fornax Global professional team meeting in Doha Qatar office boardroom"
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 60vw" />
                
                {/* Blue tint overlay */}
                <div
                  className="absolute inset-0"
                  style={{ background: 'rgba(26,86,176,0.15)' }} />
                
              </div>

              {/* Floating Badge */}
              <div
                className="float-badge absolute bottom-6 left-6 z-30 glass px-5 py-3 rounded-xl flex items-center gap-3"
                style={{ border: '1px solid rgba(0,194,255,0.3)' }}>
                
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-lg"
                  style={{ background: 'linear-gradient(135deg, #00c2ff, #1a56b0)' }}>
                  
                  🇶🇦
                </div>
                <div>
                  <div className="text-fg font-bold text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>
                    100% Qatari Owned
                  </div>
                  <div className="text-muted text-xs">Est. 2004 · Doha, Qatar</div>
                </div>
              </div>
            </div>
          </div>

          {/* Content — 40% */}
          <div className="lg:col-span-2 reveal-hidden stagger-2">
            <p className="eyebrow mb-4">WHO WE ARE</p>
            <h2
              className="text-3xl lg:text-4xl font-extrabold text-fg mb-6 leading-[1.15]"
              style={{ fontFamily: 'Syne, sans-serif' }}>
              
              Qatar's Most Trusted{' '}
              <span className="gradient-text">HR & Manpower</span>{' '}
              Partner
            </h2>
            <p
              className="text-muted text-base leading-relaxed mb-8"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 300 }}>
              
              Fornax Global Contracting WLL is a proudly 100% Qatari-owned manpower company with over 20 years powering the nation's most ambitious projects — from inception to completion across every major industry sector.
            </p>
            <p
              className="text-muted text-base leading-relaxed mb-10"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 300 }}>
              
              Our deep roots in Qatar's business landscape, combined with a global talent network spanning 15+ countries, enables us to deliver compliant, skilled, and deployment-ready workforces at scale.
            </p>

            {/* Feature Grid */}
            <div className="grid grid-cols-2 gap-4 mb-10">
              {features?.map((f) =>
              <div
                key={f?.label}
                className="flex items-center gap-3 p-3 rounded-xl transition-all duration-300 hover:border-cyan"
                style={{
                  background: 'rgba(10,36,80,0.5)',
                  border: '1px solid rgba(255,255,255,0.06)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(0,194,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'}>
                
                  <span className="text-xl">{f?.icon}</span>
                  <span className="text-fg text-sm font-medium" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {f?.label}
                  </span>
                </div>
              )}
            </div>

            <button
              onClick={() => {
                const el = document.getElementById('services');
                if (el) el?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-gradient px-7 py-3.5 rounded-full font-semibold text-sm inline-flex items-center gap-2"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              
              <span>Discover Our Story →</span>
            </button>
          </div>
        </div>
      </div>
    </section>);

}